import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { mediaSrc } from "@/lib/api";

const VOLUME = 0.175;
const FADE_MS = 4000;

const fadeIn = (a, timer) => {
  cancelAnimationFrame(timer.current);
  const t0 = performance.now();
  const step = (now) => {
    const p = Math.min(1, (now - t0) / FADE_MS);
    a.volume = VOLUME * p * p;
    if (p < 1) timer.current = requestAnimationFrame(step);
  };
  timer.current = requestAnimationFrame(step);
};
const START_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll", "wheel"];

const AmbientSound = forwardRef(function AmbientSound(_, ref) {
  const { t, settings } = useApp();
  const audioRef = useRef(null);
  const soundSrc = mediaSrc(settings?.media?.sound) || "/media/river.mp3";
  const [on, setOn] = useState(false);
  const fadeTimer = useRef(0);

  const play = () => {
    const a = audioRef.current;
    if (!a || sessionStorage.getItem("jm_sound") === "muted") return Promise.resolve(false);
    if (!a.paused) return Promise.resolve(true);
    a.volume = 0;
    return a.play().then(() => { fadeIn(a, fadeTimer); setOn(true); return true; }).catch(() => false);
  };
  useImperativeHandle(ref, () => ({ play }));

  // Try to start once the chosen sound is known; browsers that block autoplay start on the first touch, key or scroll
  const ready = settings ? soundSrc : null;
  useEffect(() => {
    if (!ready) return;
    const start = () => {
      play().then((ok) => ok && START_EVENTS.forEach((e) => window.removeEventListener(e, start)));
    };
    play().then((ok) => {
      if (!ok) START_EVENTS.forEach((e) => window.addEventListener(e, start, { passive: true }));
    });
    return () => START_EVENTS.forEach((e) => window.removeEventListener(e, start));
  }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (e) => {
    e.stopPropagation();
    if (on) {
      cancelAnimationFrame(fadeTimer.current);
      audioRef.current.pause();
      sessionStorage.setItem("jm_sound", "muted");
      setOn(false);
    } else {
      sessionStorage.removeItem("jm_sound");
      play();
    }
  };

  const Icon = on ? Volume2 : VolumeX;
  return (
    <>
      {ready && <audio ref={audioRef} src={soundSrc} loop preload="auto" />}
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={toggle}
        data-testid="audio-toggle-btn"
        aria-label={on ? t("mute") : t("unmute")}
        className={`flex h-10 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors ${
          on ? "border-emerald-300/40 bg-emerald-400/20 text-emerald-50 hover:bg-emerald-400/30" : "border-amber-300/60 bg-amber-300/20 text-amber-100 hover:bg-amber-300/30"
        } backdrop-blur-md`}
      >
        <Icon className="h-4 w-4" />
        <span data-testid="audio-toggle-label">{on ? t("mute") : t("unmute")}</span>
        {on && (
          <span className="flex h-3.5 items-end gap-[2px]">
            {[0, 1, 2].map((i) => (
              <span key={i} className="wave-bar w-[2px] rounded-full bg-emerald-300" style={{ height: "100%", animationDelay: `${i * 0.15}s` }} />
            ))}
          </span>
        )}
      </button>
    </>
  );
});

export default AmbientSound;
